import type {
  AreaDetailResponse,
  AreaReportAcceptedResponse,
  AreaReportRequest,
  AreaReportSummaryResponse,
  AreaFeatureCollection,
  AreaFeedResponse,
  HealthResponse,
  StatusResponse,
  UnmatchedResponse,
} from "./apiTypes";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message = "API request failed") {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
  }
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  const response = await fetch(path, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }
    throw new ApiRequestError(response.status, body);
  }

  return (await response.json()) as T;
}

export async function fetchAreasGeoJson(): Promise<AreaFeatureCollection> {
  return fetchJson<AreaFeatureCollection>("/api/areas.geojson", {
    headers: { Accept: "application/geo+json, application/json" },
  });
}

export async function fetchHealth(): Promise<HealthResponse> {
  return fetchJson<HealthResponse>("/api/health");
}

export async function fetchStatus(): Promise<StatusResponse> {
  return fetchJson<StatusResponse>("/api/status");
}

export async function fetchAreaDetail(key: string): Promise<AreaDetailResponse> {
  return fetchJson<AreaDetailResponse>(`/api/areas/${encodeURIComponent(key)}`);
}

export async function fetchAreaFeed(key: string, limit = 25): Promise<AreaFeedResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  return fetchJson<AreaFeedResponse>(`/api/areas/${encodeURIComponent(key)}/feed?${params.toString()}`);
}

export async function fetchUnmatched(limit = 25): Promise<UnmatchedResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  return fetchJson<UnmatchedResponse>(`/api/unmatched?${params.toString()}`);
}

export interface AreaSummary {
  key: string;
  mapped: boolean;
  displayName: string;
  status: string;
  stale: boolean;
  mapUrl?: string | null;
  /** R-672: absent on older servers means not provisional. */
  provisional?: boolean;
}

export interface AreaSummariesResponse {
  generatedAt: string;
  areas: AreaSummary[];
}

/** Display names for every area (mapped and unmapped) so lists never fall back to the tinyurl key. */
export async function fetchAreaSummaries(limit = 200): Promise<AreaSummariesResponse> {
  const params = new URLSearchParams({ mapped: "all", limit: String(limit) });
  return fetchJson<AreaSummariesResponse>(`/api/areas?${params.toString()}`);
}

/** R-458a: send a crowd report of the actual power state for an area. */
export async function postAreaReport(key: string, body: AreaReportRequest): Promise<AreaReportAcceptedResponse> {
  return fetchJson<AreaReportAcceptedResponse>(`/api/areas/${encodeURIComponent(key)}/reports`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * R-458e/R-458e1: the tally for one card. Null when the summary is disabled (404) so the control
 * shows nothing rather than an error.
 */
export async function fetchAreaReportSummary(
  key: string,
  scope: { tier: string; windowStart: string | null; windowEnd: string | null },
): Promise<AreaReportSummaryResponse | null> {
  const query = new URLSearchParams({ tier: scope.tier });
  if (scope.windowStart !== null) query.set("windowStart", scope.windowStart);
  if (scope.windowEnd !== null) query.set("windowEnd", scope.windowEnd);
  try {
    return await fetchJson<AreaReportSummaryResponse>(`/api/areas/${encodeURIComponent(key)}/reports/summary?${query}`);
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) {
      return null;
    }
    throw error;
  }
}
